import { OTPInput, type SlotProps } from 'input-otp';
import { motion, useAnimationControls, useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

const MAX_LENGTH = 6;
const VALID_PASTE_REGEX = /^[a-zA-Z0-9]+$/;

function OTPSlot({ char, isActive, hasFakeCaret }: SlotProps) {
  return (
    <div
      className={cn(
        'relative flex aspect-square w-full max-w-14 items-center justify-center text-foreground',
        'border border-border-strongest bg-surface text-sm rounded-xl text-center',
        isActive && 'z-10 ring-1 ring-accent ring-offset-accent'
      )}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-5 w-px animate-caret-blink bg-foreground" />
        </div>
      )}
    </div>
  );
}

export interface OTPCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** Applied to every value update (typed, pasted, programmatic). */
  transform?: (raw: string) => string;
  ariaLabel?: string;
  /** Bump (e.g. `errorCount + 1`) to play a horizontal shake — for invalid-code feedback. */
  shakeKey?: number;
}

export function OTPCodeInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  transform,
  ariaLabel = 'Verification code',
  shakeKey,
}: OTPCodeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const controls = useAnimationControls();
  const reduceMotion = useReducedMotion();
  const lastShakeKey = useRef(shakeKey);

  useEffect(() => {
    if (shakeKey === undefined || shakeKey === lastShakeKey.current) return;
    lastShakeKey.current = shakeKey;
    if (reduceMotion) return;
    controls.start({
      x: [0, -10, 10, -8, 8, -4, 4, 0],
      transition: { duration: 0.4, ease: 'easeInOut' },
    });
  }, [shakeKey, controls, reduceMotion]);

  const handleChange = (raw: string) => {
    onChange(transform ? transform(raw) : raw);
  };
  const handleComplete = (raw: string) => {
    onComplete?.(transform ? transform(raw) : raw);
  };

  useEffect(() => {
    if (disabled) return;

    const handlePaste = (e: ClipboardEvent) => {
      // Only redirect when no element owns focus — don't override a paste aimed at a focused button/link.
      const el = document.activeElement;
      if (el && el !== document.body) return;

      const pasted = e.clipboardData?.getData('text/plain') ?? '';
      const cleaned = pasted.replace(/\s+/g, '');
      if (cleaned.length !== MAX_LENGTH || !VALID_PASTE_REGEX.test(cleaned))
        return;

      e.preventDefault();
      inputRef.current?.focus();
      const next = (transform ? transform(cleaned) : cleaned).slice(
        0,
        MAX_LENGTH
      );
      // input-otp fires onComplete itself on the value transition to maxLength.
      onChange(next);
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [disabled, onChange, transform]);

  return (
    <motion.div
      className="w-full max-w-[24.75rem] sm:w-[24.75rem]"
      animate={controls}
    >
      <OTPInput
        ref={inputRef}
        maxLength={MAX_LENGTH}
        value={value}
        onChange={handleChange}
        onComplete={handleComplete}
        disabled={disabled}
        autoFocus
        aria-label={ariaLabel}
        containerClassName="grid w-full grid-cols-6 gap-2 sm:gap-3"
        render={({ slots }) => (
          <>
            {slots.map((slot, idx) => (
              <OTPSlot key={idx} {...slot} />
            ))}
          </>
        )}
      />
    </motion.div>
  );
}
